import { createTheme, type PaletteMode, alpha } from "@mui/material";

export function buildTheme(mode: PaletteMode) {
  const isLight = mode === "light";

  const primary = isLight
    ? { main: "#f99600", light: "#ffb518", dark: "#cc6f00" }
    : { main: "#f99600", light: "#ffca4a", dark: "#a85102" };

  return createTheme({
    palette: {
      mode,
      primary,
      secondary: { main: isLight ? "#78909c" : "#b0bec5" },
      ...(isLight
        ? { background: { default: "#fafafa", paper: "#ffffff" } }
        : { background: { default: "#0a0a0a", paper: "#1a1a1a" } }),
    },
    typography: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      h1: { fontFamily: '"Outfit", sans-serif' },
      h2: { fontFamily: '"Outfit", sans-serif' },
      h3: { fontFamily: '"Outfit", sans-serif' },
      h4: { fontFamily: '"Outfit", sans-serif' },
      h5: { fontFamily: '"Outfit", sans-serif' },
      h6: { fontFamily: '"Outfit", sans-serif' },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none" as const,
            fontWeight: 600,
            borderRadius: 8,
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: theme.shadows[4],
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: theme.shadows[1],
            },
          }),
          contained: ({ theme }) => ({
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 16,
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            "&:hover": {
              boxShadow: theme.shadows[6],
            },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            margin: "2px 8px",
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
            "&.Mui-selected": {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.22),
              },
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
              "& .MuiListItemText-primary": {
                fontWeight: 600,
                color: theme.palette.primary.main,
              },
            },
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
            },
          },
        },
      },
    },
  });
}
